import { ProductVariantDetails } from '@/models/product-variant-details/product-variant-details';
import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AppService } from '@services/app.service';
import JsBarcode from 'jsbarcode';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-barcode-print-page',
  templateUrl: './barcode-print-page.component.html',
  styleUrls: ['./barcode-print-page.component.scss']
})
export class BarcodePrintPageComponent implements OnInit, AfterViewInit {
  @ViewChild('printWrapper') printWrapper!: ElementRef;

  searchText;
  destroy$: Subject<boolean> = new Subject<boolean>();
  getAllVariantDetails: any[] = [];
  showVariantPopup = false;
  selectedProduct: any = null;
  sareeVariantCount: number = 1;
  products: any[] = [];

  constructor(
    private router: Router,
    private appService: AppService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService
  ) {
  }

  ngOnInit() {
    this.getVariantAndDiscountDetails();
  }


  // get product + variant + discount details
  getVariantAndDiscountDetails() {
    this.spinner.show();
    this.appService.getVariantAndDiscountDetails()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any[]) => {
          this.spinner.hide();

          // filter & sort based on productVariantDetails
          this.getAllVariantDetails = data
            .filter(x => x.productVariantDetails?.isAvailable === true)
            .sort(
              (a, b) =>
                b.productVariantDetails.productVariantId -
                a.productVariantDetails.productVariantId
            );

          // map printable products
          this.products = this.getAllVariantDetails.map(x => ({
            productId: x.productVariantDetails.productId,
            productVariantId: x.productVariantDetails.productVariantId,
            barcodeNumber: x.productVariantDetails.barcodeNumber?.trim() || '',
            productName: x.productVariantDetails.productName,
            size: x.productVariantDetails.size,
            mrp: x.productVariantDetails.productMrp,
            srp: x.productVariantDetails.productSrp,
            varientQuantity: x.productVariantDetails.varientQuantity,
            discountDetails: x.discounts ?? []
          }));
        },

        error: err => {
          this.spinner.hide();
          console.error(err);
          this.toastr.error('Something went wrong');
        }
      });
  }


  ngAfterViewInit(): void { }

  printSelectedProduct(item: any) {
    // Open modal instead of direct print
    this.selectedProduct = item;
    this.sareeVariantCount = Number(item.varientQuantity || 1); // auto-set input value
    this.showVariantPopup = true;
  }

  cancelVariantPopup() {
    this.showVariantPopup = false;
    this.selectedProduct = null;
  }

  // Confirm button handler
confirmVariantCount() {

  const selected = this.selectedProduct;
  const qty = Number(this.sareeVariantCount || 1);

  if (!selected) return;

  const productId = selected.productId;

  // ✅ Find all variants with same productId
  const sameProductVariants = this.products.filter(
    (p: any) => p.productId === productId
  );

  // ✅ Get discount from any variant under same product
  let productLevelDiscounts: any[] = [];

  for (const variant of sameProductVariants) {
    if (variant.discountDetails && variant.discountDetails.length > 0) {
      productLevelDiscounts = variant.discountDetails;
      break;
    }
  }

  let printHtml = `
  <html>
  <head>
    <title>Print</title>
    <style>
      @page { 
        size: 60mm 40mm;
        margin: 0; 
      }

      body { 
        margin: 0; 
        padding: 0; 
      }

      .label {
        width: 60mm;
        height: 40mm;
        box-sizing: border-box;
        padding: 3mm;
        page-break-after: always;
        text-align: center;
      }

      svg {
        width: 90%;
        height: 22mm;
      }

      .text {
        font-size: 11px;
        margin-top: 1mm;
      }

      .discount-row {
        display: flex;
        justify-content: center;
        gap: 5mm;
        margin-top: 1mm;
        font-size: 9px;
        white-space: nowrap;
      }
    </style>
  </head>
  <body>
  `;

  for (let i = 0; i < qty; i++) {

    printHtml += `
      <div class="label">
        <svg id="barcode-${i}"></svg>

        <div class="text">
          <div>${selected.barcodeNumber}</div>
          <div>${selected.productName}</div>
          <div>Rate: ₹ ${this.formatAmount(selected.srp)}</div>

          ${productLevelDiscounts.length > 0 ? `
            <div class="discount-row">
              ${productLevelDiscounts.map((d: any) => `
                <span>
                  ${d.discountQuantity}'s -
                  ₹ ${this.formatAmount(d.discountAmount)}
                </span>
              `).join('')}
            </div>
          ` : ''}
        </div>
      </div>
    `;
  }

  printHtml += `
    <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
    <script>
      ${Array.from({ length: qty }).map((_, i) =>
        `JsBarcode("#barcode-${i}", "${selected.barcodeNumber}", {
          format: "CODE128",
          width: 1.2,
          height: 55,
          displayValue: false,
          margin: 0
        });`
      ).join('')}
    </script>
  </body>
  </html>
  `;

  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  printWindow.document.open();
  printWindow.document.write(printHtml);
  printWindow.document.close();

  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
  };

  printWindow.onafterprint = () => {
    printWindow.close();
  };

  this.showVariantPopup = false;
  this.selectedProduct = null;
}

bulkPrintByProduct(productId: number) {

  const groupedItems = this.getAllVariantDetails
    .filter(p => p.productVariantDetails.productId === productId);

  if (!groupedItems.length) {
    this.toastr.warning('No variants found');
    return;
  }

  // ✅ Get discount from any variant that has it
  let productLevelDiscounts: any[] = [];

  for (const item of groupedItems) {
    if (item.discounts && item.discounts.length > 0) {
      productLevelDiscounts = item.discounts;
      break;
    }
  }

  let printHtml = `
  <html>
  <head>
    <title>Bulk Print</title>
    <style>
      @page {
        size: 60mm 40mm;
        margin: 0;
      }

      body {
        margin: 0;
        padding: 0;
      }

      .label {
        width: 60mm;
        height: 40mm;
        box-sizing: border-box;
        padding: 3mm;
        page-break-after: always;
        text-align: center;
      }

      svg {
        width: 90%;
        height: 22mm;
      }

      .text {
        font-size: 11px;
        margin-top: 1mm;
      }

      .discount-row {
        display: flex;
        justify-content: center;
        gap: 5mm;
        margin-top: 1mm;
        font-size: 9px;
        white-space: nowrap;
      }
    </style>
  </head>
  <body>
  `;

  let barcodeScripts = '';

  groupedItems.forEach((wrapper, index) => {

    const variant = wrapper.productVariantDetails;
    const qty = Number(variant.varientQuantity || 1);

    for (let i = 0; i < qty; i++) {

      const barcodeId = `barcode-${index}-${i}`;

      printHtml += `
        <div class="label">

          <svg id="${barcodeId}"></svg>

          <div class="text">
            <div>${variant.barcodeNumber || ''}</div>
            <div>${variant.productName || ''}</div>
            <div>Rate: ₹ ${this.formatAmount(variant.productSrp)}</div>

            ${productLevelDiscounts.length
              ? `
                <div class="discount-row">
                  ${productLevelDiscounts.map((d: any) => `
                    <span>
                      ${d.discountQuantity}'s -
                      ₹ ${this.formatAmount(d.discountAmount)}
                    </span>
                  `).join('')}
                </div>
              `
              : ''
            }
          </div>

        </div>
      `;

      if (variant.barcodeNumber) {
        barcodeScripts += `
          JsBarcode("#${barcodeId}", "${variant.barcodeNumber}", {
            format: "CODE128",
            width: 1.2,
            height: 55,
            displayValue: false,
            margin: 0
          });
        `;
      }
    }
  });

  printHtml += `
    <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
    <script>
      ${barcodeScripts}
    </script>
  </body>
  </html>
  `;

  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  printWindow.document.open();
  printWindow.document.write(printHtml);
  printWindow.document.close();

  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
  };

  printWindow.onafterprint = () => {
    printWindow.close();
  };
}

  formatAmount(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === '') {
      return '0.00';
    }

    const num = Number(value);
    if (isNaN(num)) return '0.00';

    return num.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }


}
